import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { checkWord, syncDictionaryToRedis } from '../../api/dictionary';
import { useLoader } from '../../context/LoaderContext';

const Dictionary = () => {
    const { showLoader, hideLoader } = useLoader();
    const [word, setWord] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    const handleCheck = async (e) => {
        e.preventDefault();
        if (!word.trim()) {
            toast.error("Lütfen bir kelime girin.");
            return;
        }

        showLoader();
        setLoading(true);
        setResult(null);
        try {
            const res = await checkWord(word);
            setResult(res);
            if (res.isTrue) {
                toast.success(res.message || "Kelime doğrulandı.");
            } else {
                toast.error(res.message || "Kelime bulunamadı.");
            }
        } catch (error) {
            console.error("Check error:", error);
            toast.error(error.message || "Kontrol sırasında hata oluştu.");
            setResult({ isTrue: false, message: error.message, source: 'error' });
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    const handleSyncRedis = async () => {
        showLoader();
        setSyncLoading(true);
        try {
            const res = await syncDictionaryToRedis();
        } catch (error) {
            console.error("Sync error:", error);
            toast.error(error.message || "Senkronizasyon başarısız.");
        } finally {
            setSyncLoading(false);
            hideLoader();
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <h6 className="text-lg fw-semibold mb-0">Sözlük</h6>
                <button
                    onClick={handleSyncRedis}
                    disabled={syncLoading}
                    className="btn btn-warning-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                >
                    <Icon icon="heroicons:circle-stack" className="text-xl" />
                    {syncLoading ? 'Gönderiliyor...' : "Redis'e Veri Gönder"}
                </button>
            </div>
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="text-center mb-24">
                            <span className="w-64-px h-64-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center mb-16">
                                <Icon icon="heroicons:book-open" className="text-4xl" />
                            </span>
                            <h5 className="mb-8">Kelime Kontrolü</h5>
                            <p className="text-secondary-light">
                                Veritabanında bir kelimenin varlığını kontrol etmek için aşağıya yazın.
                            </p>
                        </div>

                        <form onSubmit={handleCheck} className="position-relative mb-24">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    placeholder="Kelime giriniz..."
                                    value={word}
                                    onChange={(e) => setWord(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    className="btn btn-primary-600 px-24"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    ) : (
                                        <>
                                            Kontrol Et <Icon icon="heroicons:magnifying-glass" className="ms-2" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {result && (
                            <div className={`alert ${result.isTrue ? 'alert-success' : 'alert-danger'}`} role="alert">
                                <div className="d-flex align-items-center gap-3">
                                    <div className={`w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${result.isTrue ? 'bg-success-100 text-success-600' : 'bg-danger-100 text-danger-600'}`}>
                                        <Icon icon={result.isTrue ? "heroicons:check" : "heroicons:x-mark"} className="text-2xl" />
                                    </div>
                                    <div>
                                        <h6 className="alert-heading fw-bold mb-1">
                                            {result.madde_duz || word} {result.isTrue ? "Mevcut" : "Bulunamadı"}
                                        </h6>
                                        {result.message && <p className="mb-0">{result.message}</p>}
                                    </div>
                                </div>

                                {result.isTrue && (
                                    <div className="mt-16 pt-16 border-top border-success-200">
                                        <div className="d-flex flex-wrap gap-2 mb-16">
                                            {result.ozel_mi && (
                                                <span className="badge bg-primary-100 text-primary-600 radius-8 px-12 py-6">Özel İsim</span>
                                            )}
                                            {result.cogul_mu && (
                                                <span className="badge bg-primary-100 text-primary-600 radius-8 px-12 py-6">Çoğul</span>
                                            )}
                                            {result.lisan && (
                                                <span className="badge bg-neutral-100 text-neutral-600 radius-8 px-12 py-6">Dil: {result.lisan}</span>
                                            )}
                                            {typeof result.anlam_say === 'number' && (
                                                <span className="badge bg-neutral-100 text-neutral-600 radius-8 px-12 py-6">{result.anlam_say} anlam</span>
                                            )}
                                        </div>

                                        {Array.isArray(result.anlamlar) && result.anlamlar.length > 0 && (
                                            <div className="d-flex flex-column gap-2">
                                                {result.anlamlar.map((a, index) => (
                                                    <div key={index} className="bg-white radius-8 p-12 border border-success-100">
                                                        <div className="d-flex align-items-start gap-2">
                                                            <span className="badge bg-success-600 text-white radius-circle w-24-px h-24-px d-flex align-items-center justify-content-center flex-shrink-0 p-0">
                                                                {a.anlam_sira}
                                                            </span>
                                                            <span className="text-dark">{a.anlam}</span>
                                                        </div>
                                                        {(a.fiil === "1" || a.tipkes === "1" || a.gos === "1") && (
                                                            <div className="d-flex flex-wrap gap-2 mt-8 ps-32">
                                                                {a.fiil === "1" && <small className="badge bg-info-100 text-info-600 radius-4">Fiil</small>}
                                                                {a.tipkes === "1" && <small className="badge bg-info-100 text-info-600 radius-4">Tıpkes</small>}
                                                                {a.gos === "1" && <small className="badge bg-info-100 text-info-600 radius-4">Gös</small>}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {(result.source || result.processed_date || result.gosterim_tarihi) && (
                                    <div className="d-flex flex-wrap gap-3 mt-12 pt-12 border-top border-success-200 border-opacity-50">
                                        {result.source && (
                                            <small className="opacity-75">Kaynak: {result.source}</small>
                                        )}
                                        {result.processed_date && (
                                            <small className="opacity-75">İşlenme Tarihi: {result.processed_date}</small>
                                        )}
                                        {result.gosterim_tarihi && (
                                            <small className="opacity-75">Gösterim Tarihi: {result.gosterim_tarihi}</small>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dictionary;
